import {createApolloProvider} from '@vue/apollo-option'
import {ApolloClient, HttpLink, from, split, InMemoryCache} from '@apollo/client/core'
import {setContext} from '@apollo/client/link/context'
import {GraphQLWsLink} from '@apollo/client/link/subscriptions'
import {getMainDefinition} from '@apollo/client/utilities'
import {createClient} from 'graphql-ws'
import auth from 'shared/plugins/auth'

function getAuth() {
	if (!auth?.isAuthenticated.value || !auth.user.value) return ''
	return 'Bearer ' + auth.user.value.access_token
}

const getHeaders = () => {
	const Authorization = getAuth()
	if (!Authorization) return {}
	return {
		headers: {
			Authorization,
		},
	}
}

const httpLink = new HttpLink({
	uri: import.meta.env.VUE_APP_GRAPHQL_HTTP,
})

const wsClient = createClient({
	url: import.meta.env.VUE_APP_GRAPHQL_HTTP.replace('https', 'wss'),
	connectionParams: getHeaders,
})
const wsLink = new GraphQLWsLink(wsClient)

const authMiddleware = setContext(getHeaders)

const link = from([
	authMiddleware,
	split(
		// split based on operation type
		({query}) => {
			const definition = getMainDefinition(query)
			return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
		},
		wsLink,
		httpLink
	),
])

export function createProvider() {
	const apolloClient = new ApolloClient({
		link,
		cache: new InMemoryCache(),
		connectToDevTools: false,
		defaultOptions: {
			watchQuery: {
				skipPollAttempt: () => document.hidden, // or !document.hasFocus()
			},
		},
	})

	// Create vue apollo provider
	const apolloProvider = createApolloProvider({
		defaultClient: apolloClient,
		errorHandler(error, vm, operationName, operationType, operationDetails) {
			// start-failed means "start received before the connection is initialised" which happens
			if (error?.extensions?.code === 'start-failed') {
				// eslint-disable-next-line no-console
				return console.log('websocket start failed, ignoring error.')
			}

			const errorMessage = operationType + ' "' + operationName + '" ' + error.toString()

			if (import.meta.env.MODE === 'development') {
				// eslint-disable-next-line no-console
				console.error(errorMessage, {variables: operationDetails.variables})
			}

			// Sentry was complaining about capturing errors that were "non error objects"
			// https://docs.sentry.io/platforms/javascript/guides/remix/troubleshooting/#events-with-non-error-exception
			let originalErrorObject
			if (error instanceof Error === false && typeof error === 'object') {
				originalErrorObject = {...error}
				error = Object.assign(new Error(errorMessage), error)
			}

			error.context = {
				operation: {operationName, operationType, variables: operationDetails.variables},
				operationDetails,
			}
			if (originalErrorObject) error.context.originalErrorObject = originalErrorObject
			throw error
		},
	})

	return apolloProvider
}

// Manually call this when user log in
export async function onLogin(apolloClient) {
	if (wsClient) await wsClient.dispose()
	try {
		await apolloClient.resetStore()
	} catch (e) {
		// eslint-disable-next-line no-console
		console.log('%cError on cache reset (login)', 'color: orange;', e.message)
	}
}

// Manually call this when user log out
export async function onLogout(apolloClient) {
	if (wsClient) await wsClient.dispose()
	try {
		await apolloClient.resetStore()
	} catch (e) {
		// eslint-disable-next-line no-console
		console.log('%cError on cache reset (logout)', 'color: orange;', e.message)
	}
}
